import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SystemPreviewSection = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const features = [
    { icon: "ri-flashlight-line", text: "リアルタイム処理", sub: "Real-time" },
    { icon: "ri-shield-check-line", text: "金融グレードセキュリティ", sub: "Security" },
    { icon: "ri-links-line", text: "API連携対応", sub: "Integration" },
    { icon: "ri-pie-chart-2-line", text: "高度な分析機能", sub: "Analytics" },
  ];

  return (
    <section ref={ref} className="relative bg-white overflow-hidden">
      {/* Diagonal top from dark */}
      <div className="absolute top-0 left-0 right-0 h-24 bg-slate-950"
        style={{ clipPath: "polygon(0 0, 100% 0, 0 100%)" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24 lg:pb-32">
        {/* Full-width image banner first */}
        <div
          className={`relative mb-16 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          {/* Skewed image container */}
          <div
            className="relative h-64 lg:h-96 overflow-hidden rounded-3xl"
            style={{ clipPath: "polygon(0 0, 100% 0, 97% 100%, 3% 100%)" }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=advanced-financial-analytics-dashboard-dark-theme-multiple-monitors-real-time-payment-data-charts-graphs-professional-fintech-software-interface-cyan-accent-colors-clean-modern-UI-no-people&width=1400&height=600&seq=home_system_v2&orientation=landscape"
              alt="System dashboard"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/60 via-transparent to-slate-900/60" />

            {/* Overlay label */}
            <div className="absolute top-6 left-10 flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-cyan-300 text-xs font-semibold tracking-widest uppercase">LIVE SYSTEM</span>
            </div>

            {/* Floating metrics */}
            <div className="absolute bottom-6 left-10 flex gap-4 flex-wrap">
              {[
                { label: "照合精度", val: "99.8%" },
                { label: "処理件数/日", val: "12,847" },
                { label: "稼働率", val: "99.99%" },
              ].map((m) => (
                <div key={m.label} className="bg-slate-900/80 backdrop-blur-sm rounded-xl px-4 py-2 border border-slate-700/50">
                  <div className="text-xs text-slate-400">{m.label}</div>
                  <div className="text-sm font-bold text-cyan-400">{m.val}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Text + features: side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left: label + headline */}
          <div
            className={`lg:col-span-5 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}
          >
            <div className="w-1 h-12 bg-gradient-to-b from-cyan-500 to-transparent mb-4" />
            <span className="text-cyan-600 text-xs font-bold tracking-[0.3em] uppercase block mb-3">{t("top.system.label")}</span>
            <h2 className="text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-6">
              {t("top.system.title")}
            </h2>
            <p className="text-slate-600 text-base leading-relaxed mb-8">
              {t("top.system.desc")}
            </p>
            <Link
              to="/system"
              className="inline-flex items-center gap-3 px-7 py-3.5 bg-cyan-500 text-white font-bold rounded-full hover:bg-cyan-400 transition-all duration-300 cursor-pointer whitespace-nowrap group text-sm"
            >
              {t("top.system.btn")}
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </div>

          {/* Right: feature grid */}
          <div
            className={`lg:col-span-7 transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}
          >
            <div className="grid grid-cols-2 gap-4">
              {features.map((f, i) => (
                <div
                  key={f.text}
                  className={`group p-6 rounded-2xl border border-slate-100 hover:border-cyan-200 hover:bg-cyan-50/30 transition-all duration-300 ${
                    i % 2 === 1 ? "mt-6" : ""
                  }`}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-900 group-hover:bg-cyan-500 transition-colors duration-300 mb-4">
                    <i className={`${f.icon} text-cyan-400 group-hover:text-white text-base transition-colors duration-300`} />
                  </div>
                  <div className="text-sm font-bold text-slate-900 mb-0.5">{f.text}</div>
                  <div className="text-xs text-slate-400">{f.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SystemPreviewSection;
